import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import DriverCard from "@/components/DriverCard";
import WalkoutReveal from "@/components/WalkoutReveal";
import { playTick, playWhoosh, playPop, setSfxEnabled } from "@/lib/sfx";
import { getCachedTiers, fetchTiers } from "@/lib/tierConfig";

const LS_KEY = "sf_revealed_driver_ids";
const LS_MUTE = "sf_reveal_muted";

const loadRevealed = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};
const saveRevealed = (ids) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(ids)); } catch { /* noop */ }
};

// Tier-Farben gespiegelt aus DriverCard.jsx TIER_STYLES, damit Silhouetten
// strikt die echte Tier-Farbgebung der Karten reflektieren.
const TIER_STYLE_MAP = {
  bronze:    { accent: "#CD7F32", light: "#E0995C", base: "#2A1610" },
  silver:    { accent: "#C0C0C0", light: "#E8E8E8", base: "#1A1A20" },
  gold:      { accent: "#D4AF37", light: "#F3CD5D", base: "#2A2210" },
  whitegold: { accent: "#B8860B", light: "#FFFDF0", base: "#1F1D18" },
  rookie:    { accent: "#64748B", light: "#94A3B8", base: "#15171C" },
  champion:  { accent: "#A855F7", light: "#E9D5FF", base: "#1B1230" },
};

const hexToRgbStr = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [128, 128, 128];
};
const lightenHex = (hex, amt = 0.45) => {
  const rgb = hexToRgbStr(hex);
  const out = rgb.map((v) => Math.round(v + (255 - v) * amt));
  return `#${out.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};

const pickTier = (driver, tiers) => {
  if (!tiers?.length) return { ...TIER_STYLE_MAP.silver, key: "silver" };
  // Rookie & Champion haben Vorrang vor OVR-basiertem Matching (matched DriverCard-Logik)
  if (driver?.is_rookie) {
    const cfg = tiers.find(t => t.key === "rookie") || {};
    const style = TIER_STYLE_MAP.rookie;
    if (cfg.accent_color) {
      return { key: "rookie", accent: cfg.accent_color, light: lightenHex(cfg.accent_color, 0.55), base: style.base };
    }
    return { key: "rookie", ...style };
  }
  if (driver?.is_champion) {
    const cfg = tiers.find(t => t.key === "champion") || {};
    const style = TIER_STYLE_MAP.champion;
    if (cfg.accent_color) {
      return { key: "champion", accent: cfg.accent_color, light: lightenHex(cfg.accent_color, 0.55), base: style.base };
    }
    return { key: "champion", ...style };
  }
  const ovr = driver?.ovr || 0;
  const ranked = tiers.filter(t => t.key !== "rookie" && t.key !== "champion").slice().sort((a, b) => (b.min_ovr || 0) - (a.min_ovr || 0));
  const match = ranked.find(t => ovr >= (t.min_ovr || 0)) || ranked[ranked.length - 1] || {};
  const key = match.key || "silver";
  const style = TIER_STYLE_MAP[key] || TIER_STYLE_MAP.silver;
  if (match.accent_color) {
    return { key, accent: match.accent_color, light: lightenHex(match.accent_color, 0.55), base: style.base };
  }
  return { key, ...style };
};

// Silhouette für die CS:GO Reel-Karte: verdeckt, Farbverlauf in echten Tier-Farben.
function CardSilhouette({ tier }) {
  const accent = tier?.accent || "#6B7280";
  const light = tier?.light || lightenHex(accent, 0.5);
  const base = tier?.base || "#0B0F19";
  return (
    <div className="w-[180px] h-[260px] mx-auto">
      <div
        className="relative w-full h-full chamfer overflow-hidden flex flex-col items-center justify-center"
        style={{
          background: `linear-gradient(160deg, ${light} 0%, ${accent} 45%, ${base} 100%)`,
          border: `3px solid ${accent}`,
          boxShadow: `0 0 40px ${accent}cc, inset 0 -60px 80px rgba(0,0,0,0.55), inset 0 30px 50px ${light}33`,
        }}
      >
        {/* Diagonal-Stripes Texture (matched DriverCard) */}
        <div
          className="absolute inset-0 opacity-30 pointer-events-none mix-blend-overlay"
          style={{ backgroundImage: `repeating-linear-gradient(45deg, #000 0 2px, transparent 2px 14px)` }}
        />
        {/* Top Glow Bar */}
        <div
          className="absolute top-0 left-0 right-0 h-1.5 pointer-events-none"
          style={{ background: `linear-gradient(90deg, transparent, ${light}, ${accent}, ${light}, transparent)` }}
        />
        {/* Big "?" Mark */}
        <div
          className="font-display font-black italic text-[160px] leading-none"
          style={{ color: "rgba(0,0,0,0.32)", letterSpacing: "-0.08em" }}
        >
          ?
        </div>
        <div className="absolute bottom-3 left-0 right-0 text-center">
          <div
            className="font-display font-black italic uppercase text-[9px] tracking-[0.3em]"
            style={{ color: "rgba(0,0,0,0.55)" }}
          >
            Sector Four
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CardRevealPage() {
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const [drivers, setDrivers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [revealedIds, setRevealedIds] = useState(loadRevealed);
  const [packState, setPackState] = useState("idle"); // idle | spinning | walkout
  const [reelOffset, setReelOffset] = useState(0);
  const [reelItems, setReelItems] = useState([]);
  const [winnerDriver, setWinnerDriver] = useState(null);
  const [tiers, setTiers] = useState(getCachedTiers());
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(LS_MUTE) === "1"; } catch { return false; }
  });
  const reelRef = useRef(null);

  useEffect(() => { fetchTiers().then(t => setTiers(t || [])).catch(() => {}); }, []);

  useEffect(() => {
    setSfxEnabled(!muted);
    try { localStorage.setItem(LS_MUTE, muted ? "1" : "0"); } catch { /* noop */ }
  }, [muted]);

  // initialer Sync (für isSfxEnabled-Konsumenten falls jemand vor Mount fragt)
  useEffect(() => { setSfxEnabled(!muted); }, [muted]);

  useEffect(() => {
    Promise.all([
      api.get("/drivers").then(x => x.data).catch(() => []),
      api.get("/teams").then(x => x.data).catch(() => []),
    ]).then(([d, t]) => { setDrivers(d || []); setTeams(t || []); });
  }, []);

  const teamsById = useMemo(() => Object.fromEntries(teams.map(t => [t.id, t])), [teams]);
  const pool = settings?.card_reveal_pool || [];
  const poolDrivers = useMemo(
    () => drivers.filter(d => pool.includes(d.id)),
    [drivers, pool]
  );
  const remainingDrivers = useMemo(
    () => poolDrivers.filter(d => !revealedIds.includes(d.id)),
    [poolDrivers, revealedIds]
  );
  const revealedDrivers = useMemo(
    () => poolDrivers.filter(d => revealedIds.includes(d.id)),
    [poolDrivers, revealedIds]
  );

  if (!settings?.card_reveal_enabled) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <div className="eyebrow text-amber-300">Reveal-Event noch nicht gestartet</div>
        <div className="font-display font-black italic uppercase text-4xl md:text-6xl mt-3">Bald geht&apos;s los</div>
        <p className="text-neutral-400 mt-4 max-w-md">Der große Karten-Reveal ist gerade nicht aktiv. Schau später nochmal vorbei.</p>
        <button onClick={() => navigate("/")} className="mt-6 px-5 py-2 border border-white/20 hover:border-white/60 uppercase text-xs tracking-widest">Zur Startseite</button>      </div>
    );
  }

  if (poolDrivers.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <div className="eyebrow text-amber-300">Reveal ist aktiv</div>
        <div className="font-display font-black italic uppercase text-4xl mt-3">Aktuell keine Karten im Pool</div>
        <p className="text-neutral-400 mt-4">Der Admin hat noch keine Karten für den Reveal freigegeben.</p>
      </div>
    );
  }

  const allRevealed = remainingDrivers.length === 0;

  const openPack = () => {
    if (allRevealed) return;
    try { playWhoosh(); } catch { /* noop */ }
    // Wähle Gewinner aus den verbleibenden Karten
    const winner = remainingDrivers[Math.floor(Math.random() * remainingDrivers.length)];
    // Reel-Items: viele zufällige Karten + Gewinner an einer bestimmten Position
    const REEL_LEN = 60;
    const WINNER_POS = 52;
    const items = [];
    for (let i = 0; i < REEL_LEN; i++) {
      if (i === WINNER_POS) items.push(winner);
      else items.push(poolDrivers[Math.floor(Math.random() * poolDrivers.length)]);
    }
    setReelItems(items);
    setWinnerDriver(winner);
    setReelOffset(0);
    setPackState("spinning");

    // Animation: nach kurzem Tick die Karten scrollen lassen
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const cardW = 200; // px (180 + 20 gap)
        // ein wenig Jitter, damit der Marker leicht versetzt landet (nicht exakt Mitte)
        const jitter = Math.floor(Math.random() * 80) - 40;
        setReelOffset(WINNER_POS * cardW + jitter);
      });
    });

    // Tick-Schedule: 30 Ticks mit easing (immer langsamer)
    const totalMs = 6500;
    const ticks = 30;
    let acc = 0;
    for (let i = 0; i < ticks; i++) {
      // ease-out kubisch: t = 1 - (1 - x)^3
      const x = i / (ticks - 1);
      const eased = 1 - Math.pow(1 - x, 3);
      const delay = eased * totalMs;
      acc = delay;
      const pitch = 1 + (1 - x) * 0.4; // höher am Anfang, tiefer zum Ende
      setTimeout(() => { try { playTick(pitch); } catch { /* noop */ } }, delay);
    }

    // Nach Spin-Dauer Walkout starten
    setTimeout(() => setPackState("walkout"), 6800);
  };

  const closeWalkout = () => {
    try { playPop(); } catch { /* noop */ }
    if (winnerDriver && !revealedIds.includes(winnerDriver.id)) {
      const next = [...revealedIds, winnerDriver.id];
      setRevealedIds(next);
      saveRevealed(next);
    }
    setPackState("idle");
    setWinnerDriver(null);
    setReelItems([]);
    setReelOffset(0);
  };

  const resetRevealed = () => {
    if (!window.confirm("Wirklich alle aufgedeckten Karten in deinem Browser zurücksetzen?")) return;
    setRevealedIds([]);
    saveRevealed([]);
  };

  return (
    <div className="min-h-screen pb-20">
      {/* Hero */}
      <div className="text-center pt-12 pb-8">
        <div className="eyebrow text-cyan-400">Sector Four Launch</div>
        <h1 className="font-display font-black italic uppercase text-5xl md:text-7xl mt-3 leading-none">Karten-Reveal</h1>
        <p className="text-neutral-400 mt-3 max-w-xl mx-auto px-4 text-sm">
          Öffne ein Pack, lass es ausrollen und entdecke die Driver Cards der neuen Saison.
        </p>
        <div className="mt-3 text-xs text-neutral-500">
          <span className="text-white font-bold">{revealedDrivers.length}</span> / {poolDrivers.length} aufgedeckt
        </div>
        <button
          type="button"
          onClick={() => setMuted(m => !m)}
          className="mt-3 text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white inline-flex items-center gap-1.5"
          data-testid="reveal-mute-toggle"
          title={muted ? "Sound einschalten" : "Sound ausschalten"}
        >
          <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>{muted ? "🔇" : "🔊"}</span>
          <span>{muted ? "Sound aus" : "Sound an"}</span>
        </button>
      </div>

      {/* Pack Opening */}
      {packState === "idle" && (
        <div className="flex flex-col items-center">
          {allRevealed ? (
            <div className="text-center">
              <div className="font-display font-black italic uppercase text-3xl text-cyan-300">🎉 Alle Karten aufgedeckt!</div>
              <p className="text-neutral-400 mt-2 text-sm">Du hast jede Karte aus dem Pool gesehen.</p>
              <button onClick={resetRevealed} className="mt-4 text-xs uppercase tracking-widest text-neutral-500 hover:text-white underline">
                Galerie zurücksetzen
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={openPack}
              data-testid="card-reveal-open-pack"
              className="group relative outline-none"
              style={{ perspective: 1200 }}
            >
              <div
                className="relative w-[260px] h-[380px] mx-auto transition-transform duration-300 group-hover:scale-105 group-active:scale-95"
                style={{
                  background: "linear-gradient(160deg, #1a1a1a 0%, #050505 60%, #00141a 100%)",
                  border: "2px solid rgba(0,229,255,0.35)",
                  boxShadow: "0 0 60px rgba(0,229,255,0.18), inset 0 0 40px rgba(0,229,255,0.08)",
                  clipPath: "polygon(8% 0, 100% 0, 100% 92%, 92% 100%, 0 100%, 0 8%)",
                }}
              >
                <div
                  className="absolute inset-0 opacity-30 pointer-events-none"
                  style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 2px, transparent 2px 12px)" }}
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="eyebrow text-cyan-400">Sector Four</div>
                  <div className="font-display font-black italic uppercase text-3xl mt-2">Pack</div>
                  <div className="font-display italic text-[140px] leading-none mt-4" style={{ color: "rgba(0,229,255,0.18)" }}>?</div>
                  <div className="mt-6 px-5 py-2 border border-cyan-400/60 text-cyan-300 uppercase tracking-widest text-xs">Klick zum Öffnen</div>
                </div>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Spinning Reel */}
      {packState === "spinning" && (
        <div className="flex flex-col items-center">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Pack wird geöffnet …</div>
          <div
            className="relative overflow-hidden border border-white/10 bg-black/60"
            style={{ width: "min(1200px, 95vw)", height: 320 }}
          >
            {/* Marker (Zentrum) */}
            <div
              className="absolute top-0 bottom-0 z-20 pointer-events-none"
              style={{
                left: "50%",
                transform: "translateX(-50%)",
                width: 3,
                background: "linear-gradient(180deg, transparent 0%, #FFD700 20%, #FFD700 80%, transparent 100%)",
                boxShadow: "0 0 24px #FFD700",
              }}
            />
            <div
              ref={reelRef}
              className="absolute top-1/2 flex items-center gap-5"
              style={{
                left: "calc(50% - 90px)", // zentriert Karte (Breite 180/2)
                transform: `translate(-${reelOffset}px, -50%)`,
                transition: "transform 6.5s cubic-bezier(0.08, 0.6, 0.1, 1)",
              }}
            >
              {reelItems.map((d, i) => {
                const t = pickTier(d, tiers);
                return (
                  <div key={`${d?.id}-${i}`} className="shrink-0" style={{ width: 180 }}>
                    <CardSilhouette tier={t} />
                  </div>
                );
              })}
            </div>
            {/* seitliche Abdunkelung */}
            <div className="absolute inset-y-0 left-0 w-40 pointer-events-none" style={{ background: "linear-gradient(90deg, #000 0%, transparent 100%)" }} />
            <div className="absolute inset-y-0 right-0 w-40 pointer-events-none" style={{ background: "linear-gradient(270deg, #000 0%, transparent 100%)" }} />
          </div>
        </div>
      )}

      {/* Aufgedeckte Karten */}
      {revealedDrivers.length > 0 && (
        <div className="mt-16 px-4 max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="eyebrow text-neutral-400">Aufgedeckt</div>
              <h2 className="font-display font-black italic uppercase text-2xl md:text-3xl mt-1">Deine Sammlung</h2>
            </div>
            <button onClick={resetRevealed} className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white">
              Zurücksetzen
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {revealedDrivers.map(d => (
              <div key={d.id} style={{ transform: "scale(0.78)", transformOrigin: "top center" }} className="-mb-20">
                <DriverCard driver={d} team={teamsById[d.team_id]} teamsById={teamsById} staticFront testId={`revealed-${d.id}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Walkout */}
      {packState === "walkout" && winnerDriver && (
        <WalkoutReveal
          reveals={[{
            driver: winnerDriver,
            team: teamsById[winnerDriver.team_id] || null,
            prev: winnerDriver,
            oldOvr: winnerDriver.ovr,
            newOvr: winnerDriver.ovr,
          }]}
          onClose={closeWalkout}
          autoMs={0}
          revealMode
        />
      )}
    </div>
  );
}
